import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import TimeEditModal from '../time/TimeEditModal'

function fmtET(iso) {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
}
function fmtHrs(h) { return h != null ? Number(h).toFixed(2) : '0.00' }
function fmtDay(dateStr) {
  var d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
function mondayOf(d) {
  var dt = new Date(d)
  var day = dt.getDay()
  var diff = day === 0 ? -6 : 1 - day
  dt.setDate(dt.getDate() + diff)
  dt.setHours(0, 0, 0, 0)
  return dt
}
function calcElapsedHrs(clockIn) {
  if (!clockIn) return 0
  return Math.max(0, (Date.now() - new Date(clockIn).getTime()) / 3600000)
}

export default function LaborerHoursPage() {
  var { user, profile } = useAuth()
  var [attendance, setAttendance] = useState([])
  var [lastPaid, setLastPaid] = useState(null)
  var [loading, setLoading] = useState(true)
  var [editModal, setEditModal] = useState(null)

  var fetchAll = useCallback(async function() {
    if (!user?.id) return
    setLoading(true)
    var [aRes, pRes] = await Promise.all([
      supabase.from('daily_attendance').select('*').eq('user_id', user.id).order('work_date', { ascending: false }).order('session_number', { ascending: true }),
      supabase.from('payroll_records').select('pay_period_end, paid_at').eq('user_id', user.id).eq('status', 'paid').order('pay_period_end', { ascending: false }).limit(1),
    ])
    setAttendance(aRes.data || [])
    setLastPaid(pRes.data && pRes.data.length > 0 ? pRes.data[0].pay_period_end : null)
    setLoading(false)
  }, [user?.id])

  useEffect(function() { fetchAll() }, [fetchAll])

  // Realtime on attendance
  useEffect(function() {
    if (!user?.id) return
    var ch = supabase.channel('laborer-hours-' + user.id)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'daily_attendance', filter: 'user_id=eq.' + user.id }, fetchAll)
      .subscribe()
    return function() { supabase.removeChannel(ch) }
  }, [user?.id, fetchAll])

  if (loading) return <div style={st.wrap}><div style={st.loading}>Loading hours...</div></div>

  // Calculate totals
  var seasonStart = profile?.season_start
  var hrsFor = function(records) {
    return records.reduce(function(s, r) {
      var hrs = parseFloat(r.total_hours) || 0
      if (r.status === 'clocked_in' && !r.clock_out && r.clock_in) hrs = calcElapsedHrs(r.clock_in)
      return s + hrs
    }, 0)
  }

  var seasonRecords = seasonStart ? attendance.filter(function(r) { return r.work_date >= seasonStart }) : attendance
  var seasonHours = hrsFor(seasonRecords)

  var todayStr = new Date().toISOString().slice(0, 10)
  var weekStartStr = mondayOf(new Date()).toISOString().slice(0, 10)
  var weekRecords = attendance.filter(function(r) { return r.work_date >= weekStartStr && r.work_date <= todayStr })
  var weekHours = hrsFor(weekRecords)

  var unpaidRecords = lastPaid ? attendance.filter(function(r) { return r.work_date > lastPaid }) : attendance
  var unpaidHours = hrsFor(unpaidRecords)

  // Group by date for daily log — current pay period (unpaid)
  var logRecords = unpaidRecords
  var byDate = {}
  logRecords.forEach(function(r) {
    if (!byDate[r.work_date]) byDate[r.work_date] = []
    byDate[r.work_date].push(r)
  })
  var logDates = Object.keys(byDate).sort().reverse()

  var threeDaysAgo = new Date(Date.now() - 3 * 86400000).toISOString().slice(0, 10)

  return (
    <div style={st.wrap}>
      <div style={st.header}>
        <div style={st.title}>MY HOURS</div>
        <div style={st.subtitle}>{profile?.full_name || 'Me'}</div>
      </div>

      {/* Big total */}
      <div style={st.hero}>
        <div style={st.heroLabel}>TOTAL HOURS THIS SEASON</div>
        <div style={st.heroVal}>{seasonHours.toFixed(1)}</div>
        <div style={st.heroSub}>hrs</div>
      </div>

      <div style={st.statsRow}>
        <div style={st.statBox}>
          <div style={st.statVal}>{weekHours.toFixed(1)}</div>
          <div style={st.statLbl}>THIS WEEK</div>
        </div>
        <div style={st.statBox}>
          <div style={Object.assign({}, st.statVal, { color: '#b48c3c' })}>{unpaidHours.toFixed(1)}</div>
          <div style={st.statLbl}>{lastPaid ? 'SINCE LAST PAYCHECK' : 'UNPAID TO DATE'}</div>
        </div>
      </div>

      <div style={st.section}>
        <div style={st.sectionTitle}>
          <span>CURRENT PAY PERIOD</span>
          {lastPaid && <span style={st.lastPaidHint}>Last paid {fmtDay(lastPaid)}</span>}
        </div>
        {logDates.length === 0 ? (
          <div style={st.empty}>No hours yet this pay period.</div>
        ) : logDates.map(function(dateStr) {
          var sessions = byDate[dateStr]
          var dayTotal = hrsFor(sessions)
          return (
            <div key={dateStr} style={st.dayCard}>
              <div style={st.dayHeader}>
                <span style={st.dayLabel}>{fmtDay(dateStr)}</span>
                <span style={st.dayTotal}>{dayTotal.toFixed(2)} hrs</span>
              </div>
              {sessions.map(function(s) {
                var isLive = s.status === 'clocked_in' && !s.clock_out
                var canEdit = s.work_date >= threeDaysAgo
                var hrs = isLive ? calcElapsedHrs(s.clock_in) : (parseFloat(s.total_hours) || 0)
                return (
                  <div key={s.id} style={st.sessionRow}>
                    {sessions.length > 1 && <span style={st.sessionLabel}>S{s.session_number}:</span>}
                    {canEdit ? (
                      <button onClick={function() { setEditModal({ record: s, field: 'clock_in' }) }} style={st.goldTime}>{fmtET(s.clock_in)}</button>
                    ) : (
                      <span style={st.plainTime}>{fmtET(s.clock_in)}</span>
                    )}
                    <span style={st.dash}>{String.fromCodePoint(0x2014)}</span>
                    {isLive ? (
                      <span style={st.live}>{String.fromCodePoint(0x23F1)} LIVE</span>
                    ) : canEdit ? (
                      <button onClick={function() { setEditModal({ record: s, field: 'clock_out' }) }} style={st.goldTime}>{fmtET(s.clock_out)}</button>
                    ) : (
                      <span style={st.plainTime}>{fmtET(s.clock_out)}</span>
                    )}
                    <span style={st.sessHrs}>({fmtHrs(hrs)})</span>
                    {s.edited_at && <span style={st.editBadge}>Edited</span>}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      <div style={st.periodTotal}>
        <span style={st.periodLabel}>THIS PERIOD</span>
        <span style={st.periodVal}>{unpaidHours.toFixed(2)} hrs</span>
      </div>

      <div style={st.note}>
        Times from the past 3 days are tappable to request an edit. Older corrections require your crew lead or owner.
      </div>

      {editModal && <TimeEditModal record={editModal.record} field={editModal.field} onSave={function() { setEditModal(null); fetchAll() }} onClose={function() { setEditModal(null) }} />}
    </div>
  )
}

var st = {
  wrap: { fontFamily: "'Barlow', sans-serif", maxWidth: '560px', paddingBottom: '40px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' },
  header: { marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },

  hero: { padding: '28px 20px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '10px', textAlign: 'center', marginBottom: '10px' },
  heroLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '3px', color: 'rgba(180,140,60,0.8)' },
  heroVal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '68px', fontWeight: 800, color: '#b48c3c', lineHeight: 1, marginTop: '6px' },
  heroSub: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', letterSpacing: '3px', color: 'rgba(180,140,60,0.6)', marginTop: '2px' },

  statsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '24px' },
  statBox: { padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', textAlign: 'center' },
  statVal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#ffffff', lineHeight: 1 },
  statLbl: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginTop: '6px' },

  section: { marginBottom: '16px' },
  sectionTitle: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: 'rgba(255,255,255,0.45)', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  lastPaidHint: { fontSize: '10px', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '13px' },

  dayCard: { padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '5px', marginBottom: '4px' },
  dayHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' },
  dayLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.6)' },
  dayTotal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, color: '#ffffff' },
  sessionRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', fontSize: '13px', flexWrap: 'wrap' },
  sessionLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', color: 'rgba(255,255,255,0.3)', minWidth: '22px' },
  goldTime: { background: 'none', border: 'none', borderBottom: '1px dashed #b48c3c', color: '#b48c3c', fontSize: '13px', fontWeight: 600, fontFamily: "'Barlow', sans-serif", cursor: 'pointer', padding: '0 1px' },
  plainTime: { color: 'rgba(255,255,255,0.7)', fontSize: '13px' },
  dash: { color: 'rgba(255,255,255,0.2)', margin: '0 2px' },
  live: { color: '#22c55e', fontSize: '12px', fontWeight: 600 },
  sessHrs: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginLeft: '4px' },
  editBadge: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.12)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '2px 6px' },

  periodTotal: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', backgroundColor: 'rgba(180,140,60,0.08)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '6px', marginTop: '16px' },
  periodLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  periodVal: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '22px', fontWeight: 700, color: '#ffffff' },

  note: { fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', marginTop: '18px', textAlign: 'center' },
}
